import { Group, Text, useMantineTheme } from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import * as icons from '@sloyd/icons';
import { useRef, useState } from 'react';
import { numeric } from 'shared-base';
import { useCustomEvent } from '../../hooks/useCustomEvent';
import Button from '../Button/Button';
import { onPaste } from './DropZone.hooks';
import classes from './DropZone.module.css';
import { Left, Right, Wrapper } from './DropZone.style';
import { imageUrlToFile } from './DropZone.utils';

export type DropZoneProps = {
  disabled: boolean;
  onGenerate: (file: File, source: string) => void;
};

export function DropZone(props: DropZoneProps) {
  const { disabled } = props;
  const theme = useMantineTheme();
  const openRef = useRef<() => void>(null);
  const [file, setFile] = useState<File | null>(null);
  const [source, setSource] = useState('none');

  function onGenerate() {
    if (!file) return;
    props.onGenerate(file, source);
  }

  const handleDrop = (droppedFiles: File[]) => {
    setFile(droppedFiles[0]); // Only allow one file
    setSource('upload');
  };

  onPaste(handleDrop);

  useCustomEvent('DropZone', async ({ url }) => {
    // create file
    const newFile = await imageUrlToFile(url);
    if (!newFile) return;
    setFile(newFile);
    setSource('sample');
  });

  function renderPreview() {
    if (!file) return null;

    return (
      <>
        <div className={classes.thumbnail}>
          <img src={URL.createObjectURL(file)} alt={file.name} className={classes.imagePreview} />
          <Text fz='sm' ta='center' fw={500}>
            {file.name}
          </Text>
          <Text fz='xs' ta='center' c='dimmed'>
            {numeric(file.size, 'bytes')}
          </Text>
        </div>
        <Button disabled={disabled} color='pink' onClick={onGenerate}>
          Generate GLB
        </Button>
        <Button variant='subtle' color='pink' onClick={() => setFile(null)}>
          Re-upload
        </Button>
      </>
    );
  }

  function renderDropzone() {
    if (file) return null;

    return (
      <>
        <Dropzone
          openRef={openRef}
          onDrop={handleDrop}
          className={classes.dropzone}
          radius='md'
          accept={[MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.webp]} // Only PNG files
          maxSize={5 * 1024 ** 2}
          multiple={false} // Only allow one file
          disabled={disabled}
        >
          <div style={{ pointerEvents: 'none' }}>
            <Group justify='center'>
              <Dropzone.Accept>
                <icons.IconDownload size={50} color={theme.colors.blue[6]} stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <icons.IconX size={50} color={theme.colors.red[6]} stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <icons.IconCloudUpload size={50} stroke={1.5} />
              </Dropzone.Idle>
            </Group>

            <Text ta='center' fw={700} fz='lg' mt='xl'>
              <Dropzone.Accept>Drop the reference image here</Dropzone.Accept>
              <Dropzone.Reject>Only PNG files less than 30 MB are allowed</Dropzone.Reject>
              <Dropzone.Idle>Upload reference image</Dropzone.Idle>
            </Text>
            <Text ta='center' fz='sm' mt='xs' c='dimmed'>
              Drag&amp;drop an image or paste from clipboard.
            </Text>
            <Text ta='center' fz='sm' mt='xs' c='dimmed'>
              The file size must be less than 5 MB.
            </Text>
          </div>
        </Dropzone>

        <Button
          className={classes.control}
          size='md'
          radius='xl'
          onClick={() => openRef.current?.()}
        >
          Select file
        </Button>
      </>
    );
  }

  return (
    <Wrapper className='DropZone-wrapper' data-testid='DropZone-wrapper'>
      <Left>{renderDropzone()}</Left>
      <Right>{renderPreview()}</Right>
    </Wrapper>
  );
}

export default DropZone;
